import { useState } from "react";

export const PAGE_SIZES = [10, 25, 50] as const;

export interface Page<T> {
  /** Éléments de la page affichée. */
  items: T[];
  page: number;
  pages: number;
  total: number;
  size: number;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
}

/**
 * Pagination côté client (10 lignes par défaut, 10/25/50 au choix). Le hook s'appelle en haut du composant
 * (avant tout retour anticipé) ; `slice` découpe ensuite la liste filtrée. Quand `key` change (filtres,
 * recherche, onglet), on revient à la page 1.
 */
export function usePager(initialSize: number = PAGE_SIZES[0]) {
  const [state, setState] = useState({ page: 1, size: initialSize, key: "" });

  return {
    slice<T>(items: T[], key = ""): Page<T> {
      const pages = Math.max(1, Math.ceil(items.length / state.size));
      const sameKey = state.key === key;
      // Réinitialisation pendant le rendu (autorisé par React pour l'état du composant courant).
      if (!sameKey) setState((s) => ({ ...s, page: 1, key }));
      const page = sameKey ? Math.min(state.page, pages) : 1;
      return {
        items: items.slice((page - 1) * state.size, page * state.size),
        page,
        pages,
        total: items.length,
        size: state.size,
        setPage: (p) => setState((s) => ({ ...s, page: Math.max(1, Math.min(p, pages)) })),
        setSize: (size) => setState((s) => ({ ...s, size, page: 1 })),
      };
    },
  };
}
